import { Currency } from "@xola/ui-kit";
import { DateFormat, formatDate, getMeta, now } from "common";
import { sumBy } from "lodash";
import React, { ComponentProps, forwardRef, Fragment } from "react";
import {
    LineItemType,
    LineItemVisibility,
    Payment,
    PaymentLineItemStatus,
    PaymentMethod,
    Purchase,
    PurchaseItem,
    Seller,
} from "types";
import { useSeller } from "../../hooks/useAccount";
import { getPaymentMethodName } from "../../models/Payment";
import { NoTranslate } from "../Common/NoTranslate";
import { ReceiptFooterDetails } from "./ReceiptFooterDetails";
import { ReceiptSummaryTable } from "./ReceiptSummaryTable";
import { getAddress, getSellerCountry } from "@/models/Seller";
import { shouldUseBrowserPrintDialog } from "@/storages/PrinterStorage";

interface PurchaseReceiptProps {
    purchase: Purchase;
}

export const PurchaseReceipt = forwardRef<HTMLDivElement, PurchaseReceiptProps>(({ purchase }, ref) => {
    const seller = useSeller();

    const meta = getMeta(seller);
    const paddingLeft = meta?.features?.pos?.html?.paddingLeft;
    const paddingRight = meta?.features?.pos?.html?.paddingRight;
    const paddingBottom = meta?.features?.pos?.html?.paddingBottom;

    const paymentLineItems = getPaymentLineItems(seller, purchase);

    const paid = Math.abs(sumBy(paymentLineItems, "amount"));

    // If Pay Later use purchase amount
    const isPayLater = paymentLineItems.length === 0 && purchase.dueNow;
    const total = isPayLater ? purchase.amount : paid;

    const shortCodes = purchase.items
        .map((item) => ("shortCode" in item ? item.shortCode?.toUpperCase() : ""))
        .filter(Boolean);

    const serviceFees = purchase.lineItems.filter((lineItem) => {
        return (
            lineItem.type === LineItemType.PARTNER_FEE &&
            [LineItemVisibility.TRAVELER, LineItemVisibility.ALL].includes(lineItem.visibility)
        );
    });

    const showWiderFormat = shouldUseBrowserPrintDialog();

    return (
        <div
            ref={ref}
            style={{
                fontFamily: "Inter, Arial, Helvetica, sans-serif",
                fontSize: showWiderFormat ? 16 : 12,
                paddingLeft: `${paddingLeft}in`,
                paddingRight: `${paddingRight}in`,
                paddingBottom: `${paddingBottom}in`,
            }}
        >
            {/* Header Section */}
            <Section style={{ textAlign: showWiderFormat ? "left" : "center", fontSize: showWiderFormat ? 21 : 12 }}>
                <NoTranslate as="div" style={{ fontWeight: "bold", fontSize: showWiderFormat ? 24 : 14 }}>
                    {seller.name}
                </NoTranslate>
                {seller.phone ? <NoTranslate as="div">{seller.phone}</NoTranslate> : null}
                <NoTranslate>{formatDate(now(), DateFormat.DATE_TIME)}</NoTranslate>
                <NoTranslate as="div">{`${getAddress(seller)}, ${getSellerCountry(seller)}`}</NoTranslate>
            </Section>

            {/* Customer & Activity Details */}
            <CustomerDetails purchase={purchase} showWiderFormat={showWiderFormat} />

            {/* Item Summary */}
            {showWiderFormat &&
                purchase.items.map((item) => (
                    <Fragment key={item.id}>
                        <ActivityDetail showWiderFormat={showWiderFormat} item={item} />
                        <ReceiptSummaryTable purchase={purchase} item={item} showWiderFormat={showWiderFormat} />
                    </Fragment>
                ))}

            {!showWiderFormat && <ReceiptSummaryTable purchase={purchase} showWiderFormat={showWiderFormat} />}

            {/* Payment Summary */}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: showWiderFormat ? 16 : 12 }}>
                <tbody>
                    {serviceFees.map((lineItem) => (
                        <tr key={lineItem.id}>
                            <td colSpan={2} className="no-translate">
                                {lineItem.name}
                            </td>
                            <td style={{ fontSize: 16, padding: "7px", textAlign: showWiderFormat ? "left" : "right" }}>
                                <Currency currency={seller.currency}>{Math.abs(lineItem.amount)}</Currency>
                            </td>
                        </tr>
                    ))}

                    {serviceFees.length > 0 && (
                        <tr>
                            <td colSpan={2}>&nbsp;</td>
                        </tr>
                    )}

                    {paymentLineItems.length > 0 ? (
                        <tr>
                            <td style={{ fontWeight: "bold", width: "50%" }}>Payment</td>
                            <td style={{ width: "30%" }} />
                            <td style={{ width: "20%" }} />
                        </tr>
                    ) : null}

                    {paymentLineItems.map((lineItem) => {
                        const { id, name, amount, date, payment } = lineItem;
                        return (
                            <PaymentLineItem
                                key={id}
                                id={id}
                                name={name}
                                amount={amount}
                                date={date}
                                payment={payment}
                                showWiderFormat={showWiderFormat}
                            />
                        );
                    })}

                    <ReceiptTotals
                        total={total}
                        currency={seller.currency}
                        balance={purchase.balance}
                        showWiderFormat={showWiderFormat}
                    />

                    <ReceiptFooterDetails seller={seller} shortCodes={shortCodes} showWiderFormat={showWiderFormat} />
                </tbody>
            </table>
        </div>
    );
});

const Section = ({ style, ...rest }: ComponentProps<"div">) => {
    return <div style={{ marginBottom: 16, ...style }} {...rest} />;
};

interface CustomerDetailsProps {
    purchase: Purchase;
    showWiderFormat: boolean;
}

export const CustomerDetails = ({ purchase, showWiderFormat }: CustomerDetailsProps) => {
    return (
        <div style={{ fontSize: showWiderFormat ? 21 : 12 }}>
            {purchase.customerName && (
                <Section>
                    <div style={{ fontWeight: "bold" }}>Customer Details</div>

                    {showWiderFormat ? (
                        <NoTranslate as="div">{purchase.customerName}</NoTranslate>
                    ) : (
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                            <span>Name:</span>
                            <NoTranslate>{purchase.customerName}</NoTranslate>
                        </div>
                    )}
                </Section>
            )}

            {!showWiderFormat &&
                purchase.items.map((item) => (
                    <ActivityDetail key={item.id} item={item} showWiderFormat={showWiderFormat} />
                ))}
        </div>
    );
};

export const ActivityDetail = ({ item, showWiderFormat }: { item: PurchaseItem; showWiderFormat: boolean }) => {
    return (
        <Fragment key={item.id}>
            <Section>
                {showWiderFormat && <div style={{ fontWeight: "bold", fontSize: 21 }}>Activity Details</div>}
                <div style={{ fontWeight: showWiderFormat ? "normal" : "bold", fontSize: showWiderFormat ? 21 : 12 }}>
                    {item.name}
                </div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 8,
                        fontSize: showWiderFormat ? 21 : 12,
                    }}
                >
                    {!showWiderFormat && <span>Activity Date:</span>}

                    <NoTranslate>
                        {"arrivalDateTime" in item
                            ? formatDate(
                                  item.arrivalDateTime,
                                  item.arrivalTime === null ? DateFormat.DATE : DateFormat.DATE_TIME,
                              )
                            : null}
                    </NoTranslate>
                </div>
            </Section>
        </Fragment>
    );
};

interface PaymentLineItemType {
    id: ID;
    name: string;
    amount: number;
    payment?: Payment;
    date?: string;
    showWiderFormat?: boolean;
}

const PaymentLineItem = ({ id, name, amount, payment, date, showWiderFormat }: PaymentLineItemType) => {
    const { currency } = useSeller();

    if (payment) {
        const cashMeta = payment.meta?.cashCalculator;
        if (cashMeta?.change && cashMeta.change > 0) {
            return (
                <>
                    <tr>
                        <td style={{ paddingLeft: showWiderFormat ? 0 : 8 }} colSpan={2}>
                            {`Cash tendered (${date})`}
                        </td>

                        <td
                            style={{
                                textAlign: showWiderFormat ? "left" : "right",
                                paddingLeft: showWiderFormat ? "7px" : "",
                            }}
                        >
                            <Currency currency={currency}>{cashMeta.cash}</Currency>
                        </td>
                    </tr>
                    <tr>
                        <td style={{ paddingLeft: 8 }} colSpan={2}>
                            Change given
                        </td>

                        <td
                            style={{
                                textAlign: showWiderFormat ? "left" : "right",
                                paddingLeft: showWiderFormat ? "7px" : "",
                            }}
                        >
                            <Currency currency={currency}>{-1 * cashMeta.change}</Currency>
                        </td>
                    </tr>
                </>
            );
        }
    }

    return (
        <tr key={id}>
            <td style={{ paddingLeft: showWiderFormat ? 0 : 8 }} colSpan={2} className="no-translate">
                {name}
            </td>

            <td
                colSpan={1}
                style={{ textAlign: showWiderFormat ? "left" : "right", paddingLeft: showWiderFormat ? "7px" : "" }}
            >
                <Currency currency={currency}>{amount}</Currency>
            </td>
        </tr>
    );
};

const getPaymentLineItems = (seller: Seller, purchase: Purchase): PaymentLineItemType[] => {
    const paymentLineItems = purchase.lineItems.filter((lineItem) => {
        return (
            lineItem.status === PaymentLineItemStatus.COMPLETE &&
            (lineItem.type === LineItemType.PAYMENT || lineItem.type === LineItemType.REFUND)
        );
    });

    return paymentLineItems.map((lineItem) => {
        const date = formatDate(lineItem.createdAt, DateFormat.DATE_SHORT);
        const amount = Math.abs(lineItem.amount);

        if (lineItem.type === LineItemType.REFUND) {
            return {
                id: lineItem.id,
                name: `Refund (${date})`,
                amount: -amount,
            };
        }

        if (lineItem.payment?.method === PaymentMethod.CREDIT_CARD) {
            return {
                id: lineItem.id,
                name: `${lineItem.payment?.meta?.cardSummary?.brand ?? "CC"} *${
                    lineItem.payment?.meta?.cardSummary?.last4 ?? ""
                } (${date})`,
                amount,
            };
        }

        const item: PaymentLineItemType = {
            id: lineItem.id,
            name: lineItem.payment?.method ? `${getPaymentMethodName(seller, lineItem.payment.method)} (${date})` : "",
            amount,
        };

        if (lineItem.payment?.method === PaymentMethod.CASH) {
            item.payment = lineItem.payment;
            item.date = date;
        }

        return item;
    });
};

interface ReceiptTotalsProps {
    showWiderFormat: boolean;
    total: number;
    balance: number;
    currency: string;
}

const ReceiptTotals = ({ showWiderFormat, total, balance, currency }: ReceiptTotalsProps) => {
    const leftColStyle: React.CSSProperties = {
        textAlign: showWiderFormat ? "right" : "left",
        paddingTop: showWiderFormat ? 2 : 12,
        paddingBottom: 12,
        paddingRight: showWiderFormat ? "7px" : undefined,
    };

    const rightColStyle: React.CSSProperties = {
        textAlign: showWiderFormat ? "left" : "right",
        paddingTop: showWiderFormat ? 2 : 12,
        paddingBottom: 12,
        paddingLeft: showWiderFormat ? "7px" : undefined,
    };

    const rowStyle: React.CSSProperties = {
        fontSize: 16,
        fontWeight: "bold",
        borderTop: showWiderFormat ? undefined : "1px solid black",
    };

    return (
        <>
            <tr style={rowStyle}>
                <td colSpan={2} style={leftColStyle}>
                    Total
                </td>
                <td style={rightColStyle}>
                    <Currency currency={currency}>{total}</Currency>
                </td>
            </tr>
            {balance > 0 && (
                <tr style={rowStyle}>
                    <td style={leftColStyle} colSpan={2}>
                        Balance Due
                    </td>
                    <td style={rightColStyle}>
                        <Currency currency={currency}>{Math.abs(balance)}</Currency>
                    </td>
                </tr>
            )}
        </>
    );
};
